import { DocumentationFile } from 'store/documentation/model/types';
import { useDocumentationForm } from 'components/dataCreationForms/withDocumentationForm';
import type { DocumentationFields } from 'components/dataCreationForms/withDocumentationForm/types';
import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { DocumentationForm } from 'components/documentation/DocumentationForm';
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { handleFormValidation, validate } from './validation';

interface DocumentationEditProps {
  file: DocumentationFile;
  close: () => void;
}

function _DocumentationEdit({
  file,
  close,
}: DocumentationEditProps): ReactElement {
  const name = file.title;
  const number = file.description;
  const localId = file.localId;
  const {
    submitForm,
    isPosting,
    releaseSubmitEvent,
    SUBMIT_EVENT_TYPE,
    formName,
  } = useDocumentationForm();

  const closeRef = useRef(close);
  closeRef.current = close;

  // A documentation without a local id cannot be edited: the dialog closes.
  useEffect(() => {
    if (typeof localId !== 'number') {
      closeRef.current();
    }
  }, [localId]);

  const submitEdit = useCallback(
    (values: DocumentationFields) => submitForm(values, { documentation: file, onSuccess: close }),
    [submitForm, file, close]
  );

  const initialValues = useMemo(() => {
    return { name, number };
  }, [name, number]);

  return (
    <WithInputForm
      values={initialValues}
      errors={{}}
      handleFormValidation={handleFormValidation}
      handleFieldValidation={validate}
      onSubmit={submitEdit}
      eventType={SUBMIT_EVENT_TYPE}
      dispatchSubmit={releaseSubmitEvent}
      formName={formName}
    >
      <DocumentationForm
        isPosting={isPosting}
        onCancel={close}
        onSubmit={releaseSubmitEvent}
        file={file}
      />
    </WithInputForm>
  );
}

export const DocumentationEdit = _DocumentationEdit;
